import React from 'react';
import { useSurvey } from '../../../context/SurveyContext';
import { X, Plus, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const QuestionSettings = ({ onClose }) => {
  const { getSelectedQuestion, updateQuestion } = useSurvey();
  const question = getSelectedQuestion();

  if (!question) return null;

  const handleChange = (field, value) => {
    updateQuestion(question.id, { [field]: value });
  };

  const handleOptionChange = (optionId, text) => {
    const newOptions = question.options.map(opt =>
      opt.id === optionId ? { ...opt, text } : opt
    );
    updateQuestion(question.id, { options: newOptions });
  };

  const addOption = () => {
    const newOptions = [
      ...question.options,
      { id: uuidv4(), text: `Option ${question.options.length + 1}` }
    ];
    updateQuestion(question.id, { options: newOptions });
  };

  const removeOption = (optionId) => {
    if (question.options.length <= 2) return;
    const newOptions = question.options.filter(opt => opt.id !== optionId);
    updateQuestion(question.id, { options: newOptions });
  };

  const handleLikertChange = (index, text) => {
    const newLabels = [...question.labels];
    newLabels[index] = text;
    updateQuestion(question.id, { labels: newLabels });
  };

  const handlePointsChange = (points) => {
    const defaultLabels = {
      4: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
      5: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
      6: ['Strongly Disagree', 'Disagree', 'Slightly Disagree', 'Slightly Agree', 'Agree', 'Strongly Agree'],
      7: ['Strongly Disagree', 'Disagree', 'Somewhat Disagree', 'Neutral', 'Somewhat Agree', 'Agree', 'Strongly Agree']
    };
    updateQuestion(question.id, { points, labels: defaultLabels[points] });
  };

  return (
    <div className="question-settings">
      <div className="settings-header">
        <h3>Question Settings</h3>
        <button className="settings-close" onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      <div className="settings-group">
        <label className="settings-label">Question Text</label>
        <textarea
          className="settings-textarea"
          value={question.text}
          onChange={(e) => handleChange('text', e.target.value)}
          placeholder="Enter your question..."
        />
      </div>

      <div className="settings-group">
        <label className="settings-label">Help Text (Optional)</label>
        <input
          type="text"
          className="settings-input"
          value={question.helpText || ''}
          onChange={(e) => handleChange('helpText', e.target.value)}
          placeholder="Additional instructions..."
        />
      </div>

      <div className="settings-group">
        <div
          className="settings-toggle"
          onClick={() => handleChange('required', !question.required)}
        >
          <span className="toggle-label">Required</span>
          <div className={`toggle-switch ${question.required ? 'active' : ''}`} />
        </div>
      </div>

      {['multipleChoice', 'checkboxes', 'dropdown'].includes(question.type) && (
        <div className="settings-group">
          <label className="settings-label">Answer Options</label>
          <div className="options-list">
            {question.options?.map((option) => (
              <div key={option.id} className="option-item">
                <input
                  type="text"
                  className="option-input"
                  value={option.text}
                  onChange={(e) => handleOptionChange(option.id, e.target.value)}
                />
                <button
                  className="option-remove"
                  onClick={() => removeOption(option.id)}
                  disabled={question.options.length <= 2}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
          <button className="add-option-btn" onClick={addOption}>
            <Plus size={16} />
            Add Option
          </button>
        </div>
      )}

      {question.type === 'linearScale' && (
        <>
          <div className="settings-group">
            <label className="settings-label">Scale Range</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select
                className="settings-input"
                value={question.scaleMin || 1}
                onChange={(e) => handleChange('scaleMin', parseInt(e.target.value))}
                style={{ width: '80px' }}
              >
                <option value={0}>0</option>
                <option value={1}>1</option>
              </select>
              <span>to</span>
              <select
                className="settings-input"
                value={question.scaleMax || 5}
                onChange={(e) => handleChange('scaleMax', parseInt(e.target.value))}
                style={{ width: '80px' }}
              >
                {[5, 7, 10].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="settings-group">
            <label className="settings-label">Min Label</label>
            <input
              type="text"
              className="settings-input"
              value={question.minLabel || ''}
              onChange={(e) => handleChange('minLabel', e.target.value)}
              placeholder="e.g., Not at all"
            />
          </div>
          <div className="settings-group">
            <label className="settings-label">Max Label</label>
            <input
              type="text"
              className="settings-input"
              value={question.maxLabel || ''}
              onChange={(e) => handleChange('maxLabel', e.target.value)}
              placeholder="e.g., Extremely"
            />
          </div>
        </>
      )}

      {question.type === 'likertScale' && (
        <>
          <div className="settings-group">
            <label className="settings-label">Number of Points</label>
            <select
              className="settings-input"
              value={question.points || 5}
              onChange={(e) => handlePointsChange(parseInt(e.target.value))}
            >
              {[4, 5, 6, 7].map(n => (
                <option key={n} value={n}>{n} points</option>
              ))}
            </select>
          </div>
          <div className="settings-group">
            <label className="settings-label">Point Labels</label>
            <div className="options-list">
              {question.labels?.map((label, index) => (
                <div key={index} className="option-item">
                  <span style={{ minWidth: '20px', color: '#64748b', fontSize: '12px' }}>{index + 1}.</span>
                  <input
                    type="text"
                    className="option-input"
                    value={label}
                    onChange={(e) => handleLikertChange(index, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {question.type === 'number' && (
        <>
          <div className="settings-group">
            <label className="settings-label">Minimum Value (Optional)</label>
            <input
              type="number"
              className="settings-input"
              value={question.min ?? ''}
              onChange={(e) => handleChange('min', e.target.value ? parseInt(e.target.value) : null)}
            />
          </div>
          <div className="settings-group">
            <label className="settings-label">Maximum Value (Optional)</label>
            <input
              type="number"
              className="settings-input"
              value={question.max ?? ''}
              onChange={(e) => handleChange('max', e.target.value ? parseInt(e.target.value) : null)}
            />
          </div>
        </>
      )}

      {['shortAnswer', 'longAnswer'].includes(question.type) && (
        <div className="settings-group">
          <label className="settings-label">Max Characters</label>
          <input
            type="number"
            className="settings-input"
            value={question.maxLength || ''}
            onChange={(e) => handleChange('maxLength', e.target.value ? parseInt(e.target.value) : null)}
          />
        </div>
      )}
    </div>
  );
};

export default QuestionSettings;
